import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Target,
  Briefcase,
  Users,
  Wrench,
  ExternalLink,
  ClipboardCheck,
} from 'lucide-react';
import Recommendations from '../components/Recommendations';
import SEO from '../components/SEO';
import { STRIPE_CHECKOUT_URL, DISCOVERY_CALL_URL } from '../constants/site';
import { TermsNote } from '../components/TermsNote';

// =====================================================================
// /enrollment — Enrollment page for the 3-Day Online Training
// Restored classical layout (hero → curriculum → sessions → investment
// → promises → recommendations → CTA → alternatives) — adapted to
// the new online offer, no application form, no in-person cohort.
// =====================================================================

const CURRICULUM_DAYS = [
  {
    day: 'Day 1',
    eyebrow: 'Foundation & Operational Awareness',
    modules: [
      { n: '01', title: 'Introduction to Private Aviation', body: 'Operator landscape, market structure, the difference between charter, corporate, owner and fractional — the world you are entering.' },
      { n: '02', title: 'Aircraft Types & Cabin Environments', body: 'From Bombardier and Gulfstream to Dassault Falcon, ACJ and BBJ — cabin classes, galleys, and the operational constraints of each.' },
      { n: '03', title: 'Cabin Attendant Structures in Business Aviation', body: 'How cabin crew roles, hierarchies and responsibilities actually work across premium operators.' },
      { n: '04', title: 'UHNW Clients & Cultural Awareness', body: 'Reading principal profiles. Implicit codes, cultural sensitivities, and the discretion expected at the highest level.' },
      { n: '05', title: 'Cabin Safety & Pilot Relationship', body: 'Working with the flight deck. Cabin secure procedures. The single-cabin-attendant environment.' },
      { n: '06', title: 'Positioning & Profile Building', body: 'CV repositioning for private aviation. LinkedIn, photo standards, narrative.' },
    ],
  },
  {
    day: 'Day 2',
    eyebrow: 'Service, Execution & Recovery',
    modules: [
      { n: '07', title: 'Catering Intelligence & Operational Planning', body: 'How premium operators actually order catering. Suppliers, budgets, flight-length logic. Reinforced with a real catering order practice.' },
      { n: '08', title: 'Plating & Presentation', body: 'Standards of plate composition for in-flight service. Walkthroughs based on real operator standards.' },
      { n: '09', title: 'Wine, Spirits & Barista Service', body: 'What to know, what to master, what to skip. Practical service standards in the cabin.' },
      { n: '10', title: 'Crisis Handling & Service Recovery', body: 'When things go wrong — inflight resolution, communication with the client, keeping the standard.' },
    ],
  },
  {
    day: 'Day 3',
    eyebrow: 'Aircraft Mastery, Crisis Readiness & Interview',
    modules: [
      { n: '11', title: 'Aircraft Deep Dive · Global, Challenger, Gulfstream, Falcon', body: 'Real operator standards — VistaJet, NetJets, corporate operations. What flying each aircraft actually looks like day-to-day.' },
      { n: '12', title: 'Rotation Reality · Multi-Sector Ops', body: 'Stock control, FBO coordination, budget management, post-flight reporting. The 80% of the job that is not service.' },
      { n: '13', title: 'Interview Preparation', body: 'Operator interview mechanics. What they ask, what they listen for, how to answer.' },
      { n: '14', title: 'Final Debrief & Placement Roadmap', body: 'Your positioning, your next steps, your operator shortlist.' },
    ],
  },
];

const LIVE_SESSIONS = [
  { dates: '23 — 25 June 2026', status: 'full' },
  { dates: '28 — 30 July 2026', status: 'full' },
  { dates: '22 — 24 September 2026', status: 'full' },
  { dates: '28 — 30 September 2026', status: 'full' },
  { dates: '20 — 22 October 2026', status: 'full' },
  { dates: '27 — 29 October 2026', status: 'full' },
  { dates: '24 — 26 November 2026', status: 'last' },
  { dates: '17 — 19 December 2026', status: 'open' },
  { dates: '14 — 16 January 2027', status: 'open' },
  { dates: '20 — 22 January 2027', status: 'open' },
  { dates: '17 — 19 February 2027', status: 'open' },
];

const STATUS_STYLES = {
  open: { label: 'OPEN', class: 'text-[#1A1A1A]' },
  full: { label: 'FULL', class: 'text-[#9A9A9A] line-through' },
  last: { label: 'LAST SEATS', class: 'text-[#C5A880] font-semibold' },
};

const INCLUDED = [
  'Three consecutive live online days with Nicolas',
  '14 modules delivered live — no recordings, no self-paced videos',
  'Downloadable materials · checklists, catering templates, operator standards',
  'Post-program access to the private WhatsApp community',
];

const PROMISES = [
  'To make you operationally ready to step into a private aviation role from day one.',
  'To share our curated list of private aviation operators so your applications go in the right direction.',
  'To transform your CV, LinkedIn, and positioning so you enter the market with the right tools.',
  'To provide complete training documentation and reference materials.',
  'To support you throughout the program and beyond, as much as we can.',
];

const NOT_PROMISES = [
  'A guaranteed job. No serious program can promise that, and any program that does should make you suspicious.',
  'An overnight transition. Private aviation rewards patience and the right first move — not speed.',
  'Shortcuts around the work. The program is intensive by design.',
];

const ALT_PATHS = [
  {
    name: 'Readiness Assessment',
    icon: ClipboardCheck,
    description: 'The free 15-minute assessment. 12 real-world scenarios + 3 operator cases, manually reviewed by Nicolas.',
    expect: 'Expect: personalized written feedback, your strengths, improvement areas, and clear next steps.',
    price: 'Free',
    priceNote: '15 min · Manually reviewed',
    cta: 'Take the assessment',
    link: '/operational-assessment',
    external: false,
  },
  {
    name: 'Discovery Call',
    icon: Target,
    description: "Not sure if the training is the right move? Book a free 45-minute call. We'll assess your profile and confirm alignment before you enroll.",
    expect: 'Expect: an honest profile assessment, what is holding you back, and a clear next move.',
    price: 'Free',
    priceNote: '45 min · No obligation',
    cta: 'Book your discovery call',
    link: DISCOVERY_CALL_URL,
    external: true,
  },
  {
    name: '3-Month Mentorship',
    icon: Users,
    description: 'Three months of structured 1:1 guidance to reposition your profile, apply to the right operators, and prepare for the interviews that matter.',
    expect: 'Expect: monthly 1:1 calls, continuous adjustment, interview coaching, and curated operator access.',
    price: '€2,200',
    priceNote: 'Package · 3 months',
    cta: 'Book a screening call',
    link: '/mentorship',
    external: false,
  },
  {
    name: 'Catering & Operations Training',
    icon: Wrench,
    description: 'A focused one-day online training on catering intelligence and onboard operations for private aviation.',
    expect: 'Expect: the operational layer no airline ever taught you — from flight-prep to catering ordering.',
    price: '€490',
    priceNote: '1 day · Live online',
    cta: 'Discover the training',
    link: '/academy/catering-operations',
    external: false,
  },
];

// =====================================================================
// Page
// =====================================================================
const EnrollmentPage = () => {
  useEffect(() => {
    document.title = 'Enrollment · Gate 33';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="enrollment-page" className="min-h-screen">
      <SEO
        title="Enrollment — 3-Day Online Training"
        description="Reserve your seat in the next Gate 33 live cohort. €600 or 3 × €200. Free discovery call available before enrollment."
        path="/enrollment"
      />
      {/* ===== SECTION 1 — HERO ===== */}
      <section
        data-testid="enrol-hero"
        className="relative min-h-[70vh] flex items-center"
      >
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/pd3x5f8l_global7500_173338570_d14162cfb8_332857f3fd.webp"
            alt="Gate 33 — 3-Day Online Training"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/85 to-[#FDFBF7]/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="max-w-2xl">
            <span className="overline inline-block mb-6">3-Day Online Training</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
              Master the Reality of Private Aviation
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed mb-10">
              A three-day live online cohort preparing experienced cabin crew and hospitality
              professionals for the operational standards, expectations, and recruitment realities
              of business aviation.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <a
                href={STRIPE_CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="enrol-hero-reserve-cta"
                onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'enrol_hero' })}
                className="btn-primary inline-flex items-center gap-2"
              >
                Reserve my seat — €600 <ArrowRight size={16} />
              </a>
              <a
                href={DISCOVERY_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="enrol-hero-cta"
                className="btn-secondary inline-flex items-center gap-2"
              >
                Book a Discovery Call <ArrowRight size={16} />
              </a>
              <Link
                to="/operational-assessment"
                data-testid="enrol-hero-quiz-cta"
                className="text-xs uppercase tracking-[0.18em] text-[#1A1A1A] hover:text-[#C5A880] transition-colors"
              >
                or take the assessment →
              </Link>
            </div>
            <TermsNote testId="enrol-hero-terms-note" className="mt-3" />
          </div>
        </div>
      </section>

      {/* ===== SECTION 2 — CURRICULUM ===== */}
      <section data-testid="enrol-curriculum" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline inline-block mb-4">Program Curriculum</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
              Comprehensive Operational Preparation
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Fourteen modules across three days — from operational foundation to crisis
              readiness and final operator interview. Every session is live with Nicolas,
              directly interactive, and built on real operator standards.
            </p>
          </div>

          <div className="space-y-16">
            {CURRICULUM_DAYS.map((day) => (
              <div
                key={day.day}
                data-testid={`enrol-curriculum-${day.day.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex flex-wrap items-baseline gap-4 mb-8 pb-5 border-b border-[#E5E0D8]">
                  <span className="font-heading text-2xl text-[#0F0F0F]">{day.day}</span>
                  <span className="overline text-[#1A1A1A]">{day.eyebrow}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                  {day.modules.map((m) => (
                    <div
                      key={m.n}
                      data-testid={`enrol-module-${m.n}`}
                      className="flex items-start gap-5 border-l-2 border-[#C5A880] pl-5 py-1"
                    >
                      <span className="font-heading text-2xl text-[#C5A880] flex-shrink-0 leading-none w-10">
                        {m.n}
                      </span>
                      <div>
                        <h3 className="font-heading text-lg mb-2 text-[#0F0F0F] leading-snug">
                          {m.title}
                        </h3>
                        <p className="text-[#5A5A5A] text-sm leading-relaxed">{m.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 3 — LIVE SESSIONS ===== */}
      <section data-testid="enrol-cohorts" className="section-padding bg-[#FDFBF7]">
        <div className="container-custom max-w-5xl">
          <div className="max-w-2xl mb-14">
            <span className="overline inline-block mb-4">Live Session Calendar</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
              Limited seats. Confirmed in order.
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Each live cohort is capped to preserve the quality of direct interaction with Nicolas.
              Seats are confirmed in the order candidates enroll.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0 border-t border-[#E5E0D8]">
            {LIVE_SESSIONS.map((c, i) => {
              const s = STATUS_STYLES[c.status];
              return (
                <div
                  key={c.dates}
                  data-testid={`enrol-cohort-${i}`}
                  className="flex items-center justify-between gap-4 py-5 border-b border-[#E5E0D8]"
                >
                  <span className={`font-heading text-lg ${s.class}`}>{c.dates}</span>
                  <div className="flex items-center gap-4">
                    <span
                      className={`text-xs uppercase tracking-[0.18em] ${
                        c.status === 'last'
                          ? 'text-[#C5A880]'
                          : c.status === 'full'
                          ? 'text-[#9A9A9A]'
                          : 'text-[#1A1A1A]'
                      }`}
                    >
                      {s.label}
                    </span>
                    {c.status !== 'full' && (
                      <a
                        href={STRIPE_CHECKOUT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`enrol-cohort-${i}-reserve`}
                        onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'enrol_cohorts', cohort: c.dates })}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold border border-[#0F0F0F] px-3 py-1.5 text-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-[#FDFBF7] transition-all"
                      >
                        Reserve <ArrowRight size={10} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SECTION 4 — INVESTMENT ===== */}
      <section data-testid="enrol-investment" className="section-padding bg-white">
        <div className="container-custom max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="overline inline-block mb-5">Investment</span>
              <div className="font-heading text-6xl sm:text-7xl lg:text-8xl tracking-tight leading-none text-[#0F0F0F] mb-3">
                600<span className="text-[#C5A880]">€</span>
              </div>
              <p className="overline text-[#5A5A5A] mb-6">or up to 24× payments with PayPal</p>
              <p className="text-sm text-[#5A5A5A] leading-relaxed max-w-md">
                Access begins on your chosen live session dates. No hidden fees.
              </p>
            </div>
            <div>
              <p className="overline text-[#1A1A1A] mb-6">What is included</p>
              <ul className="space-y-4">
                {INCLUDED.map((it) => (
                  <li
                    key={it}
                    className="flex items-start gap-3 text-[#1A1A1A] leading-relaxed"
                  >
                    <span className="text-[#C5A880] mt-0.5">—</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5 — PROMISES / NOT PROMISES ===== */}
      <section data-testid="enrol-promises" className="section-padding bg-[#FDFBF7]">
        <div className="container-custom max-w-6xl">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="overline inline-block mb-4">Transparent by Design</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
              What we promise. What we don&rsquo;t.
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Transparency matters more than marketing. We tell candidates exactly what they can
              expect — and what they should never expect from any serious training program.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            <div className="bg-white border border-[#E5E0D8] p-8 md:p-10">
              <p className="overline text-[#C5A880] mb-6">What Gate 33 promises</p>
              <ul className="space-y-5">
                {PROMISES.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-3 text-[#1A1A1A] leading-relaxed"
                  >
                    <CheckCircle2
                      size={16}
                      className="text-[#C5A880] flex-shrink-0 mt-1"
                      strokeWidth={1.8}
                    />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-[#E5E0D8] p-8 md:p-10">
              <p className="overline text-[#5A5A5A] mb-6">What we don&rsquo;t promise</p>
              <ul className="space-y-5">
                {NOT_PROMISES.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-3 text-[#5A5A5A] leading-relaxed"
                  >
                    <span className="text-[#9A9A9A] flex-shrink-0 mt-0.5">×</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-center font-heading text-xl sm:text-2xl italic text-[#1A1A1A] max-w-3xl mx-auto mt-14 leading-relaxed">
            &ldquo;What we guarantee is preparation, positioning, and direction. The market work is yours.&rdquo;
          </p>
        </div>
      </section>

      {/* ===== SECTION 6 — REVIEWS ===== */}
      <Recommendations centered />

      {/* ===== SECTION 7 — PRIMARY CTA ===== */}
      <section
        id="enrol-cta"
        data-testid="enrol-cta-section"
        className="section-dark py-20 md:py-24 border-y border-[#C5A880]/40 scroll-mt-20"
      >
        <div className="container-custom text-center max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-5">
            Next step
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            Ready to enroll?
          </h2>
          <p className="text-[#D6D2CA] max-w-xl mx-auto mb-10 leading-relaxed">
            Book a free 45-minute discovery call. We&rsquo;ll confirm alignment with your profile,
            answer your questions, and lock your preferred live session dates.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href={STRIPE_CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enrol-cta-reserve"
              onClick={() => window.gtag?.('event', 'checkout_clicked', { location: 'enrol_final' })}
              className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-12 py-5 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
            >
              Reserve my seat — €600 <ArrowRight size={16} />
            </a>
            <a
              href={DISCOVERY_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enrol-cta-discovery"
              className="inline-flex items-center justify-center gap-2 border border-[#C5A880] text-[#C5A880] px-12 py-5 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
            >
              Book a Discovery Call <ArrowRight size={16} />
            </a>
          </div>
          <TermsNote testId="enrol-cta-terms-note" className="mt-4" dark />
          <p className="text-xs uppercase tracking-[0.2em] text-[#9A9A9A] mt-6">
            Live cohorts · Limited seats
          </p>
        </div>
      </section>

      {/* ===== SECTION 8 — ALTERNATIVE PATHS ===== */}
      <section data-testid="enrol-alternatives" className="section-padding bg-white">
        <div className="container-custom max-w-6xl">
          <div className="max-w-2xl mb-14">
            <span className="overline inline-block mb-4">Other paths</span>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight mb-5">
              Not sure the Training is right for you yet?
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Four other entry points depending on where you are in your transition.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {ALT_PATHS.map((p) => {
              const Icon = p.icon;
              const cardBody = (
                <article
                  data-testid={`enrol-alt-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="border border-[#E5E0D8] p-7 md:p-9 flex flex-col hover:border-[#C5A880] transition-colors duration-300 h-full"
                >
                  <div className="flex items-start justify-between gap-4 mb-5">
                    <Icon size={22} className="text-[#C5A880] flex-shrink-0" strokeWidth={1.5} />
                    <div className="text-right">
                      <div className="font-heading text-3xl md:text-4xl tracking-tight leading-none text-[#0F0F0F]">
                        {p.price}
                      </div>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-[#5A5A5A] mt-2">
                        {p.priceNote}
                      </p>
                    </div>
                  </div>
                  <h3 className="font-heading text-lg text-[#0F0F0F] mb-3 leading-snug">
                    {p.name}
                  </h3>
                  <p className="text-sm text-[#5A5A5A] leading-relaxed mb-3">
                    {p.description}
                  </p>
                  <p className="text-sm text-[#1A1A1A] leading-relaxed mb-7 flex-1 border-l-2 border-[#C5A880] pl-3">
                    {p.expect}
                  </p>
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-[#1A1A1A] group-hover:text-[#C5A880] transition-colors self-start">
                    {p.cta} {p.external ? <ExternalLink size={12} /> : <ArrowRight size={12} />}
                  </span>
                </article>
              );
              return p.external ? (
                <a
                  key={p.name}
                  href={p.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  {cardBody}
                </a>
              ) : (
                <Link key={p.name} to={p.link} className="group">
                  {cardBody}
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnrollmentPage;
